import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageCard from "../../components/page-card";
import { translate } from "../../i18n";
import { CoachAvailabilityDto } from "../../models/coach/dto/coach-availability.dto";
import { CoachingSessionStatus } from '../../models/coach/dto/coaching-session.dto';
import { GetCoachCalendarOutputDto } from "../../models/coach/dto/get-coach-calendar.dto";
import CreateCoachAvailabilityModal from "../coach-calendar/components/CreateCoachAvailabilityModal";
import SelectedCoachAvailabilityModal from "../coach-calendar/components/SelectedCoachAvailabilityModal";
import MonthlyCourtCalendar from "../court-details/components/MonthlyCalendar";
import { useSelectedOrganization } from "../organization/hooks/useSelectedOrganization";
import SetCoachAvailabilitiesModal from "./components/SetCoachAvailabilitiesModal";
import { COACH_CALENDAR_QUERY_KEY, useGetCoachCalendar } from "./hooks/useGetCoachCalendar";
import { useGetCurrentAuthenticatedCoach } from "./hooks/useGetCurrentAuthenticatedCoach";

export enum CalendarEventType {
    Game,
    Blockade,
    CoachingSession
}


type Props = {
    coachId: string;
    // Availability editing is allowed for admins and for the coach's OWN
    // calendar (API: hasCoachSelfOrOrganizationAdminAccess). Callers pass
    // false when a coach views another coach's calendar.
    canManage?: boolean;
}
export function CoachCalendar({ coachId, canManage = true }: Props) {
    const navigate = useNavigate();
    const [dateBoundary, setDateBoundary] = useState(() => getInitialDateBoundary());
    const [showCanceledSessions, setShowCanceledSessions] = useState(false);

    const { data: coachCalendar } =
        useGetCoachCalendar(coachId, dateBoundary.start, dateBoundary.end);

    const events = useMemo(() => {
        const filteredSessions = showCanceledSessions
            ? (coachCalendar?.coachingSessions || [])
            : (coachCalendar?.coachingSessions || []).filter(session => session.status !== CoachingSessionStatus.CANCELED);

        return filteredSessions.map(g => {
            const isCanceled = g.status === CoachingSessionStatus.CANCELED;
            const titlePrefix = isCanceled ? `[${translate('calendar.canceled')}] ` : '';
            return {
                start: new Date(g.startTime),
                end: new Date(g.endTime),
                title: `${titlePrefix}${translate('calendar.coaching_sessions')}`,
                eventType: CalendarEventType.CoachingSession,
                coachingSession: g
            }
        });
    }, [coachCalendar, showCanceledSessions]);

    const backgroundEvents = useMemo(() => {
        return (coachCalendar?.availabilities || []).map(a => {
            return {
                start: new Date(a.startTime),
                end: new Date(a.endTime),
                title: translate('calendar.available'),
                availability: a
            }
        });
    }, [coachCalendar]);

    const onRangeChange = useCallback((start: Date, end: Date) => {
        setDateBoundary({ start, end })
    }, []);

    const queryClient = useQueryClient();

    const onCoachAvailabilityAdded = useCallback((newAvailabilities: CoachAvailabilityDto[]) => {
        queryClient.setQueryData<GetCoachCalendarOutputDto>(COACH_CALENDAR_QUERY_KEY(coachId, dateBoundary.start, dateBoundary.end), (newData) => {
            return { ...newData, availabilities: [...newData.availabilities, ...newAvailabilities] };
        });
        queryClient.invalidateQueries(COACH_CALENDAR_QUERY_KEY(coachId, dateBoundary.start, dateBoundary.end));
    }, [queryClient, coachId, dateBoundary]);

    const onAvailabilityDeleted = useCallback((availabilityId: string) => {
        queryClient.setQueryData<GetCoachCalendarOutputDto>(COACH_CALENDAR_QUERY_KEY(coachId, dateBoundary.start, dateBoundary.end), (newData) => {
            return { ...newData, availabilities: newData.availabilities.filter(availability => availability.id !== availabilityId) };
        });
        queryClient.invalidateQueries(COACH_CALENDAR_QUERY_KEY(coachId, dateBoundary.start, dateBoundary.end));
    }, [queryClient, coachId, dateBoundary]);

    const [selectedAvailability, setSelectedAvailability] = useState<CoachAvailabilityDto>();
    const [isAvailabilityModalVisible, setIsAvailabilityModalVisible] = useState(false);

    const [isAddingAvailabilitiesModalVisible, setIsAddingAvailabilitiesModalVisible] = useState(false);
    const [selectedTimeSlot, setSelectedTimeslot] = useState<{ startTime: Date; endTime: Date; coachId: string }>();
    const onEventSelected = useCallback((event: { availability?: CoachAvailabilityDto; coachingSession?: { id: string } }) => {
        if (event.availability) {
            setSelectedAvailability(event.availability);
            setIsAvailabilityModalVisible(true);
        } else if (event.coachingSession) {
            navigate(`/admin/coachingSessionDetails?coachingSession=${event.coachingSession.id}`);
        }
    }, [navigate]);

    const onTimeSlotSelected = useCallback((start: Date, end: Date) => {
        if (!canManage) return;
        setSelectedTimeslot({
            startTime: start,
            endTime: end,
            coachId
        })
        setIsAddingAvailabilitiesModalVisible(true);
    }, [canManage, coachId]);

    return (
        <PageCard
            showBackButton
            title={translate('court.calendar')}
            rightHeader={
                canManage ? <SetCoachAvailabilitiesModal coachId={coachId} /> : null
            }>
            <SelectedCoachAvailabilityModal
                isModalVisible={isAvailabilityModalVisible}
                setIsModalVisible={setIsAvailabilityModalVisible}
                selectedAvailability={selectedAvailability}
                onCoachAvailabilityDeleted={onAvailabilityDeleted}
                canManage={canManage}
            />
            <CreateCoachAvailabilityModal
                isModalVisible={isAddingAvailabilitiesModalVisible}
                setIsModalVisible={setIsAddingAvailabilitiesModalVisible}
                coachId={coachId}
                selectedTimeslot={selectedTimeSlot}
                onAvailabilitiesAdded={onCoachAvailabilityAdded}
            />
            <MonthlyCourtCalendar
                onTimeSlotSelected={onTimeSlotSelected}
                onEventSelected={onEventSelected}
                backgroundEvents={backgroundEvents}
                onRangeChange={onRangeChange}
                events={events}
            />
        </PageCard>
    );
}

function getInitialDateBoundary() {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const start = new Date(currentYear, currentMonth, 1);
    while (start.getDay() !== 0) {
        start.setDate(start.getDate() - 1);
    }
    start.setHours(0, 0, 0, 0);

    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    while (lastDay.getDay() !== 6) {
        lastDay.setDate(lastDay.getDate() + 1);
    }
    const end = new Date(lastDay);
    end.setHours(23, 59, 59, 999);

    return { start, end };
}

export default function CoachCalendarPage() {
    const { selectedOrganization } = useSelectedOrganization();
    const { data: coach } = useGetCurrentAuthenticatedCoach(selectedOrganization?.id);
    const coachId = coach?.id;
    if (!coach) return null;

    return (
        <CoachCalendar coachId={coachId} />
    );
}
